import { toDate } from '../timeUtils';

/**
 * Recipe-control utility helpers used by RecipeDisplay and FeedTimestamping correction flows.
 * Functions in this module are intentionally pure and side-effect free.
 *
 * Production events are standalone Firestore documents at:
 *   clients/{clientId}/client_collections/recipe_control/production_events/{eventId}
 *
 * Each event has:
 *   type: 'time_correction' | 'material_correction' | 'production_instruction'
 *   device_id: string          (originating device — all types)
 *   production_id: string      (associated production run — all types)
 *   device_ids: string[]       (all affected devices — material_correction only)
 */

/**
 * Determines whether a production event should be considered a pending material correction.
 *
 * @param {Object} correction - Event payload.
 * @param {Object} [options={}]
 * @param {boolean} [options.requireMaterials=false] - Require non-empty materials array.
 * @returns {boolean} True when event is a material_correction and not executed.
 */
export function isPendingMaterialCorrection(correction, options = {}) {
  const { requireMaterials = false } = options;
  const correctionType = String(correction?.type || '')
    .trim()
    .toLowerCase();
  const isMaterialCorrection = correctionType === 'material_correction';
  const isExecuted =
    correction?.executed === true || Boolean(correction?.executed_timestamp) || Boolean(correction?.executed_by);
  const hasMaterials = Array.isArray(correction?.materials) && correction.materials.length > 0;
  return isMaterialCorrection && !isExecuted && (!requireMaterials || hasMaterials);
}

/**
 * Finds the currently running production run for the provided timestamp.
 *
 * @param {Array<Object>} [productionRuns=[]] - Production runs to scan.
 * @param {Date} [now=new Date()] - Reference timestamp.
 * @returns {Object|null} Currently running run or null.
 */
export function getCurrentRunningProduction(productionRuns = [], now = new Date()) {
  const nowTimestamp = now?.getTime ? now.getTime() : Date.now();
  return (
    productionRuns.find((run) => {
      const start = toDate(run?.start_date);
      const end = toDate(run?.end_date);
      return start && end && nowTimestamp >= start.getTime() && nowTimestamp <= end.getTime();
    }) || null
  );
}

/**
 * Where a production run sits relative to now.
 *
 * Production runs carry no status field — a run is defined only by its window — so the status is
 * derived from that window, using the same inclusive boundaries as `getCurrentRunningProduction`
 * so the two never disagree about which run is live.
 *
 * @param {Object|null} productionRun - Production run with `start_date` and `end_date`.
 * @param {Date} [now=new Date()] - Reference timestamp.
 * @returns {'running'|'upcoming'|'finished'|null} Status, or null when the window is unusable.
 */
export function getProductionRunStatus(productionRun, now = new Date()) {
  const start = toDate(productionRun?.start_date);
  const end = toDate(productionRun?.end_date);
  if (!start || !end) return null;

  const nowTimestamp = now?.getTime ? now.getTime() : Date.now();
  if (nowTimestamp < start.getTime()) return 'upcoming';
  if (nowTimestamp > end.getTime()) return 'finished';
  return 'running';
}

/**
 * Finds the newest pending material correction from a flat events array.
 *
 * An event whose `created_at` is missing or unreadable sorts last: it cannot be shown to be the
 * latest instruction, so it must never displace one that can. Such an event is still returned when
 * it is the only pending correction.
 *
 * @param {Array<Object>} [events=[]] - Production event documents.
 * @param {Object} [options={}]
 * @param {boolean} [options.requireMaterials=false] - Require non-empty materials array.
 * @returns {Object|null} Newest pending material correction or null.
 */
export function getNewestPendingMaterialCorrection(events = [], options = {}) {
  const { requireMaterials = false } = options;
  const list = Array.isArray(events) ? events : [];
  const createdAtMillis = (correction) => toDate(correction?.created_at)?.getTime() ?? Number.MIN_SAFE_INTEGER;

  return (
    list
      .filter((correction) => isPendingMaterialCorrection(correction, { requireMaterials }))
      .sort((a, b) => createdAtMillis(b) - createdAtMillis(a))[0] || null
  );
}

/**
 * Filters production events to pending material corrections for a specific device.
 *
 * @param {Array<Object>} [events=[]] - Production event documents.
 * @param {string} deviceId - Device ID to match against event.device_ids.
 * @returns {Array<Object>} Pending material correction events for the device.
 */
export function getMaterialCorrectionsForDevice(events = [], deviceId) {
  if (!deviceId || !Array.isArray(events)) return [];
  return events.filter(
    (e) =>
      e?.type === 'material_correction' &&
      e?.executed !== true &&
      Array.isArray(e?.device_ids) &&
      e.device_ids.includes(deviceId),
  );
}
